import {
    Avatar,
    Backdrop,
    Box,
    Button,
    Fade,
    IconButton,
    Stack,
    styled,
    SvgIcon,
    Typography,
    useTheme,
    Zoom,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useLocation } from 'react-router-dom';
import {
    ChevronDownIcon,
    PlusIcon,
    Bars3Icon,
    XMarkIcon,
} from '@heroicons/react/24/solid';
import { useSidebar } from './SidebarContext';
import { SIDE_NAV_WIDTH } from './constants';

export type PageDescription = {
    title?: string;
    body?: ReactNode;
};

export type TopbarProps = {
    pageDescription?: PageDescription | null;
    explanationVideo?: string | null;
    resetPageData?: () => void;
    userInitial?: string;
    renderAccountPopover?: (props: {
        anchorEl: HTMLElement | null;
        open: boolean;
        onClose: () => void;
    }) => ReactNode;
    rightSlot?: ReactNode;
};

const ExplanationButtons = styled(Button)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(9),
    color: theme.palette.secondary.dark,
    borderColor: theme.palette.secondary.dark,
    '&:hover': {
        borderColor: theme.palette.secondary.dark,
    },
}));

export function Topbar({
    pageDescription,
    explanationVideo,
    resetPageData,
    userInitial,
    renderAccountPopover,
    rightSlot,
}: TopbarProps) {
    const theme = useTheme();
    const [accordionExpanded, setAccordionExpanded] = useState(false);
    const [videoOverlayOpen, setVideoOverlayOpen] = useState(false);
    const [accountPopoverOpen, setAccountPopoverOpen] = useState(false);
    const accountAnchorRef = useRef<HTMLButtonElement>(null);
    const { pathname } = useLocation();
    const { toggleMobileSidebar } = useSidebar();

    const transitionDuration = {
        enter: theme.transitions.duration.enteringScreen,
        exit: theme.transitions.duration.leavingScreen,
    };

    const toggleAccordion = () => setAccordionExpanded((prev) => !prev);

    useEffect(() => {
        setAccordionExpanded(false);
        resetPageData?.();
    }, [pathname, resetPageData]);

    return (
        <>
            <Box
                component='header'
                sx={{
                    position: 'fixed',
                    zIndex: (t) => t.zIndex.appBar,
                    left: {
                        xs: 0,
                        lg: `${SIDE_NAV_WIDTH}px`,
                    },
                    top: 0,
                    width: {
                        xs: '100%',
                        lg: `calc(100% - ${SIDE_NAV_WIDTH}px)`,
                    },
                }}
            >
                <Accordion
                    square
                    sx={{
                        backdropFilter: 'blur(4px)',
                        backgroundColor: (t) =>
                            alpha(t.palette.background.default, 0.8),
                        transition: theme.transitions.create([
                            'background-color',
                            'backdrop-filter',
                            'box-shadow',
                        ]),
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0,
                        borderBottomRightRadius: 8,
                        borderBottomLeftRadius: 8,
                        ...(accordionExpanded && {
                            backdropFilter: 'blur(0px)',
                            backgroundColor: (t) =>
                                alpha(t.palette.background.default, 1),
                            boxShadow: theme.shadows[3],
                        }),
                    }}
                    expanded={accordionExpanded}
                >
                    <AccordionSummary
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            p: 3,
                            borderRadius: 0,
                            '& .MuiAccordionSummary-content': { my: 0 },
                        }}
                    >
                        <Stack
                            sx={{ alignItems: 'center' }}
                            spacing={3}
                            direction='row'
                        >
                            <IconButton
                                aria-label='Menü öffnen'
                                onClick={toggleMobileSidebar}
                                sx={{ display: { lg: 'none' } }}
                            >
                                <SvgIcon>
                                    <Bars3Icon />
                                </SvgIcon>
                            </IconButton>
                            <Zoom
                                in={!!pageDescription}
                                timeout={transitionDuration}
                                unmountOnExit
                            >
                                <ExplanationButtons
                                    variant='outlined'
                                    color='secondary'
                                    onClick={toggleAccordion}
                                >
                                    <SvgIcon
                                        sx={{
                                            transition: (t) =>
                                                t.transitions.create([
                                                    'transform',
                                                ]),
                                            transform: `rotateZ(${accordionExpanded ? 180 : 0}deg)`,
                                        }}
                                        fontSize='small'
                                    >
                                        <ChevronDownIcon />
                                    </SvgIcon>
                                    &nbsp; Infos zur Seite
                                </ExplanationButtons>
                            </Zoom>
                            <Zoom
                                in={!!explanationVideo}
                                timeout={transitionDuration}
                                style={{
                                    transitionDelay: explanationVideo
                                        ? '80ms'
                                        : '0ms',
                                }}
                                unmountOnExit
                            >
                                <ExplanationButtons
                                    variant='outlined'
                                    onClick={() => setVideoOverlayOpen(true)}
                                >
                                    <SvgIcon fontSize='small'>
                                        <PlusIcon />
                                    </SvgIcon>
                                    &nbsp; Erklärvideo
                                </ExplanationButtons>
                            </Zoom>
                        </Stack>
                        {rightSlot ?? (
                            <Avatar
                                component='button'
                                aria-label='Konto-Menü öffnen'
                                aria-haspopup='menu'
                                color='secondary'
                                onClick={() => setAccountPopoverOpen(true)}
                                ref={accountAnchorRef}
                                sx={(t) => ({
                                    ml: 'auto',
                                    backgroundColor: t.palette.primary.main,
                                    border: 0,
                                    padding: 0,
                                    cursor: 'pointer',
                                    height: 48,
                                    width: 48,
                                    fontSize: '1.25rem',
                                })}
                            >
                                {userInitial?.toUpperCase() ?? ''}
                            </Avatar>
                        )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 10, pb: 6 }}>
                        <Fade
                            style={{
                                transitionDelay: accordionExpanded
                                    ? '80ms'
                                    : '0ms',
                            }}
                            in={accordionExpanded}
                        >
                            <div>
                                <Typography variant='h4' sx={{ mb: 6 }}>
                                    {pageDescription?.title}
                                </Typography>
                                {pageDescription?.body}
                            </div>
                        </Fade>
                    </AccordionDetails>
                </Accordion>
                {renderAccountPopover?.({
                    anchorEl: accountAnchorRef.current,
                    open: accountPopoverOpen,
                    onClose: () => setAccountPopoverOpen(false),
                })}
            </Box>
            <Backdrop
                onClick={toggleAccordion}
                sx={{ zIndex: (t) => t.zIndex.appBar - 1 }}
                open={accordionExpanded}
            />
            {explanationVideo && (
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (t) => t.zIndex.drawer + 1,
                    }}
                    open={videoOverlayOpen}
                    onClick={() => setVideoOverlayOpen(false)}
                >
                    <IconButton
                        aria-label='Schließen'
                        sx={(t) => ({
                            position: 'absolute',
                            top: t.spacing(4),
                            right: t.spacing(4),
                            color: '#fff',
                        })}
                        onClick={(e) => {
                            e.stopPropagation();
                            setVideoOverlayOpen(false);
                        }}
                    >
                        <SvgIcon>
                            <XMarkIcon />
                        </SvgIcon>
                    </IconButton>
                    <Box
                        component='iframe'
                        src={explanationVideo}
                        title='Erklärvideo'
                        sx={{
                            width: '80%',
                            minWidth: 300,
                            height: 360,
                            border: 0,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Backdrop>
            )}
        </>
    );
}
